import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ETICHETE_FEL_DOCUMENT_SG,
  ETICHETE_INTERACTIUNE,
  FELURI_INTERACTIUNE,
  type FelInteractiune,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { dataScurta, eroare, lei } from './comun'
import { type Fila, type Fisa } from './ClientPage'
import { FisaContPdf } from './FisaContPdf'

type FelPas = 'comanda' | 'factura' | 'incasare' | 'teren' | 'act' | 'task' | 'vizita'
interface Pas {
  fel: FelPas
  cand: string
  text: string
  cine: string | null
  link: string | null
  felInteractiune?: string
}

const CULOARE: Record<FelPas, string> = {
  comanda: 'bg-b4l-blue',
  factura: 'bg-b4l-purple',
  incasare: 'bg-b4l-green',
  teren: 'bg-b4l-amber',
  act: 'bg-b4l-text-dim',
  task: 'bg-b4l-red',
  vizita: 'bg-b4l-blue-dark',
}

const FILTRE: [string, string, FelPas[]][] = [
  ['tot', 'Tot', []],
  ['bani', 'Comenzi și facturi', ['comanda', 'factura']],
  ['incasari', 'Încasări', ['incasare']],
  ['teren', 'Teren și marketing', ['teren']],
  ['vizite', 'Vizite', ['vizita']],
  ['acte', 'Acte și task-uri', ['act', 'task']],
]

export function FilaPrezentare({
  f,
  poateNota,
  poatePdf,
  laFila,
}: {
  f: Fisa
  poateNota: boolean
  poatePdf: boolean
  laFila: (x: Fila) => void
}) {
  const id = f.client.id
  const [filtru, setFiltru] = useState('tot')
  const [notez, setNotez] = useState(false)
  const act = useQuery<{ pasi: Pas[] }>({
    queryKey: ['clienti', 'activitate', id],
    queryFn: async () => (await api.get(`/clienti/${id}/activitate`)).data,
  })
  const vanzari = useQuery<{
    produse: {
      productId: number
      nume: string
      qtyIn: number
      valIn: number
      qtyOut: number
      pctOut: number | null
      pret: number | null
    }[]
  }>({
    queryKey: ['clienti', 'vanzari', id, 12],
    queryFn: async () => (await api.get(`/clienti/${id}/vanzari`)).data,
  })
  const pasi = useMemo(() => {
    const feluri = FILTRE.find(([k]) => k === filtru)?.[2] ?? []
    return (act.data?.pasi ?? []).filter((p) => !feluri.length || feluri.includes(p.fel))
  }, [act.data, filtru])
  const d = f.deRezolvat
  const nimic = !d.pesteTermen.length && !d.scadente7.count && !d.acteExpira.length

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
      <div className="grid content-start gap-4">
        <section
          aria-label="De rezolvat"
          className="grid gap-2.5 rounded-xl border border-b4l-border bg-b4l-surface p-4"
        >
          <h2 className="text-[15px] font-bold text-b4l-text">De rezolvat cu clientul</h2>
          {nimic && (
            <p className="text-corp text-b4l-text-muted">
              Nimic acum: facturile sunt în termen și niciun act nu expiră curând.
            </p>
          )}
          {d.pesteTermen.map((x) => (
            <div
              key={x.docNo}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-b4l-red-light px-3 py-2 text-corp text-b4l-red-dark"
            >
              <span>
                <b>{x.docNo}</b> · {lei(x.ramas, 2)}, scadentă pe {dataScurta(x.scadenta)} —{' '}
                <b>{x.zile} zile peste termen</b>
              </span>
            </div>
          ))}
          {d.pesteTermenMaiMulte > 0 && (
            <button
              type="button"
              onClick={() => laFila('facturi')}
              className="justify-self-start text-corp text-b4l-blue-dark hover:underline"
            >
              și încă {d.pesteTermenMaiMulte} facturi peste termen
            </button>
          )}
          {d.pesteTermen.length > 0 && poatePdf && (
            <FisaContPdf
              client={f.client}
              varianta="link"
              eticheta="Fișa de cont PDF, de trimis clientului"
            />
          )}
          {d.scadente7.count > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-b4l-bg-soft px-3 py-2 text-corp text-b4l-text">
              <span>
                {d.scadente7.count} {d.scadente7.count === 1 ? 'factură ajunge' : 'facturi ajung'}{' '}
                la scadență în 7 zile · {lei(d.scadente7.suma)}
              </span>
              <button
                type="button"
                onClick={() => laFila('facturi')}
                className="text-b4l-blue-dark hover:underline"
              >
                Vezi facturile
              </button>
            </div>
          )}
          {d.acteExpira.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-b4l-amber-light px-3 py-2 text-corp text-b4l-amber-dark"
            >
              <span>
                {ETICHETE_FEL_DOCUMENT_SG[a.fel as keyof typeof ETICHETE_FEL_DOCUMENT_SG] ??
                  'Actul'}{' '}
                „{a.titlu}” expiră pe {dataScurta(a.valabilPana)}
              </span>
              <button
                type="button"
                onClick={() => laFila('acte')}
                className="font-semibold hover:underline"
              >
                Deschide actul
              </button>
            </div>
          ))}
        </section>

        <section
          aria-label="Ce cumpără"
          className="grid gap-2.5 rounded-xl border border-b4l-border bg-b4l-surface p-4"
        >
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-[15px] font-bold text-b4l-text">Ce cumpără — ultimele 12 luni</h2>
            <button
              type="button"
              onClick={() => laFila('vanzari')}
              className="text-corp text-b4l-blue-dark hover:underline"
            >
              Tot sell-in / sell-out
            </button>
          </div>
          {vanzari.isLoading ? (
            <p className="text-corp text-b4l-text-muted">Se încarcă…</p>
          ) : !(vanzari.data?.produse.length ?? 0) ? (
            <p className="text-corp text-b4l-text-muted">N-a cumpărat nimic în ultimele 12 luni.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse text-corp">
                <thead>
                  <tr className="text-left text-mic text-b4l-text-muted">
                    <th scope="col" className="py-1.5 font-medium">
                      Produs
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Cumpărat
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Valoare
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Vândut
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Preț EA
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vanzari.data!.produse.slice(0, 6).map((p) => (
                    <tr key={p.productId}>
                      <td className="border-t border-b4l-border py-2">{p.nume}</td>
                      <td className="border-t border-b4l-border py-2 text-right tabular-nums">
                        {p.qtyIn.toLocaleString('ro-RO')} buc
                      </td>
                      <td className="border-t border-b4l-border py-2 text-right tabular-nums">
                        {lei(p.valIn)}
                      </td>
                      <td className="border-t border-b4l-border py-2 text-right text-b4l-text-muted">
                        {p.pctOut == null
                          ? '—'
                          : `${p.qtyOut.toLocaleString('ro-RO')} · ${p.pctOut}%`}
                      </td>
                      <td className="border-t border-b4l-border py-2 text-right text-b4l-text-muted">
                        {p.pret != null ? lei(p.pret, 2) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <section
        aria-label="Activitate"
        className="grid content-start gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[15px] font-bold text-b4l-text">Activitate</h2>
          {poateNota && (
            <Button variant="secondary" onClick={() => setNotez(true)}>
              + Notează o vizită
            </Button>
          )}
        </div>
        <div role="group" aria-label="Filtru" className="flex flex-wrap gap-1.5">
          {FILTRE.map(([k, et]) => (
            <button
              key={k}
              type="button"
              aria-pressed={filtru === k}
              onClick={() => setFiltru(k)}
              className={`rounded-full border px-2.5 py-1 text-mic ${filtru === k ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark' : 'border-b4l-border text-b4l-text-muted hover:text-b4l-text'}`}
            >
              {et}
            </button>
          ))}
        </div>
        {act.isLoading ? (
          <p className="text-corp text-b4l-text-muted">Se încarcă…</p>
        ) : pasi.length === 0 ? (
          <p className="text-corp text-b4l-text-muted">Nimic în ultimele 12 luni.</p>
        ) : (
          <ol className="grid max-h-[720px] gap-0 overflow-y-auto pr-1">
            {pasi.map((p, i) => (
              <li
                key={`${p.cand}-${i}`}
                className="grid grid-cols-[14px_minmax(0,1fr)] gap-2.5 pb-3.5"
              >
                <span
                  className={`mt-1.5 h-2.5 w-2.5 rounded-full ${CULOARE[p.fel]}`}
                  aria-hidden="true"
                />
                <span className="grid gap-0.5">
                  <span className="text-corp text-b4l-text">
                    {p.fel === 'vizita' && p.felInteractiune && (
                      <b className="font-semibold">
                        {ETICHETE_INTERACTIUNE[p.felInteractiune as FelInteractiune] ?? 'Notă'}
                        :{' '}
                      </b>
                    )}
                    {p.link ? (
                      <Link to={p.link} className="hover:underline">
                        {p.text}
                      </Link>
                    ) : (
                      p.text
                    )}
                  </span>
                  <span className="text-mic text-b4l-text-muted">
                    {dataScurta(p.cand)}
                    {p.cine ? ` · ${p.cine}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {notez && <NoteazaVizita f={f} laInchidere={() => setNotez(false)} />}
    </div>
  )
}

function NoteazaVizita({ f, laInchidere }: { f: Fisa; laInchidere: () => void }) {
  const qc = useQueryClient()
  const azi = new Date().toISOString().slice(0, 10)
  const [fel, setFel] = useState<FelInteractiune>('vizita')
  const [data, setData] = useState(azi)
  const [nota, setNota] = useState('')
  const [punct, setPunct] = useState('')
  const salveaza = useMutation({
    mutationFn: async () =>
      api.post(`/clienti/${f.client.id}/interactiuni`, {
        fel,
        data: `${data}T12:00:00`,
        nota,
        branchId: punct ? Number(punct) : null,
      }),
    onSuccess: () => {
      toast.success('Notat.')
      void qc.invalidateQueries({ queryKey: ['clienti', 'activitate', f.client.id] })
      laInchidere()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const camp =
    'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text'
  return (
    <Modal
      open
      onClose={laInchidere}
      title={`Notează — ${f.client.nume}`}
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            onClick={() => salveaza.mutate()}
            loading={salveaza.isPending}
            disabled={nota.trim().length < 2 || data > azi}
          >
            Salvează
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-corp">
        <div role="radiogroup" aria-label="Ce a fost" className="flex flex-wrap gap-1.5">
          {FELURI_INTERACTIUNE.map((x) => (
            <button
              key={x}
              type="button"
              role="radio"
              aria-checked={fel === x}
              onClick={() => setFel(x)}
              className={`rounded-full border px-3 py-1.5 text-mic ${fel === x ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark' : 'border-b4l-border text-b4l-text'}`}
            >
              {ETICHETE_INTERACTIUNE[x]}
            </button>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="grid gap-1">
            <span className="text-mic font-semibold text-b4l-text-muted">Când</span>
            <input
              type="date"
              value={data}
              max={azi}
              onChange={(e) => setData(e.target.value)}
              className={camp}
            />
          </label>
          {f.puncte.length > 0 && (
            <label className="grid gap-1">
              <span className="text-mic font-semibold text-b4l-text-muted">Punct de lucru</span>
              <select value={punct} onChange={(e) => setPunct(e.target.value)} className={camp}>
                <option value="">— oricare —</option>
                {f.puncte.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.branchName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <label className="grid gap-1">
          <span className="text-mic font-semibold text-b4l-text-muted">Ce s-a întâmplat</span>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="ex. am verificat raftul, au cerut testere pentru noiembrie"
            className={camp}
          />
        </label>
      </div>
    </Modal>
  )
}
